import api from '../api';
import {parseZodSchema} from '../models/parseZodSchema';
import {
    customEventDefinitionView,
    paginatedCustomEventDefinitionList,
    type CreateCustomEventDefinitionInput,
    type CustomEventDefinitionView,
    type UpdateCustomEventDefinitionInput,
} from '../models/customEvents/customEventDefinition';
import type {DataTableResult} from '../../admin/components/tables/dataTable/dataTableModel';
import type {DataTableParams} from '../../admin/components/tables/dataTable/types';

const BASE_URL = '/api/intent';

export type CustomEventProperties = Record<string, string | number | boolean | null>;

export function getCustomEventDefinitions(
    tableParams: DataTableParams
): Promise<DataTableResult<CustomEventDefinitionView>> {
    const page = tableParams.limit > 0 ? Math.floor(tableParams.offset / tableParams.limit) + 1 : 1;
    const size = tableParams.limit;

    return api
        .get(`${BASE_URL}/event-definitions?page=${page}&size=${size}`)
        .then(api.handle)
        .catch(api.error)
        .then(raw => parseZodSchema(raw, paginatedCustomEventDefinitionList));
}

export function addCustomEventDefinition(
    input: CreateCustomEventDefinitionInput
): Promise<CustomEventDefinitionView> {
    return api
        .postJSON(`${BASE_URL}/event-definition`, input)
        .then(api.handle)
        .catch(api.error)
        .then(raw => parseZodSchema(raw, customEventDefinitionView));
}

export function getCustomEventDefinition(id: string): Promise<CustomEventDefinitionView> {
    return api
        .get(`${BASE_URL}/event-definition/${id}`)
        .then(api.handle)
        .catch(api.error)
        .then(raw => parseZodSchema(raw, customEventDefinitionView));
}

export function updateCustomEventDefinition(
    id: string,
    input: UpdateCustomEventDefinitionInput
): Promise<CustomEventDefinitionView> {
    return api
        .put(`${BASE_URL}/event-definition/${id}`, input)
        .then(api.handle)
        .catch(api.error)
        .then(raw => parseZodSchema(raw, customEventDefinitionView));
}

export function updateCustomEventDefinitionActive(
    id: string,
    active: boolean
): Promise<CustomEventDefinitionView> {
    return api
        .patch(`${BASE_URL}/event-definition/${id}`, {active})
        .then(api.handle)
        .catch(api.error)
        .then(raw => parseZodSchema(raw, customEventDefinitionView));
}

export function emitCustomEvent(
    eventName: string,
    properties: CustomEventProperties,
    occurredAt?: Date
): Promise<unknown> {
    const payload: {
        eventName: string;
        properties: CustomEventProperties;
        occurredAt?: string;
    } = {eventName, properties};

    if (occurredAt) {
        payload.occurredAt = occurredAt.toISOString();
    }

    return api.postJSON(`${BASE_URL}/event`, payload).then(api.handle).catch(api.error);
}
